import { useCallback, useEffect, useRef, useState } from "react"

import { transport, useTransportMutation } from "@/transport"
import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { KeepPersonaVoiceCandidateResponse } from "@/types_api/backend/transport/keep_persona_voice_candidate_transport_response"

const log = logger("usePersonaVoiceDesign")

export const PERSONA_VOICE_DESIGN_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "pt", label: "Portuguese" },
  { value: "de", label: "German" },
] as const

export type PersonaVoiceDesignLanguage =
  (typeof PERSONA_VOICE_DESIGN_LANGUAGES)[number]["value"]

/** Fixed line used to audition generated voice candidates. */
const PERSONA_VOICE_AUDITION_TEXT =
  "Hi there, thanks so much for calling. How can I help you today?"

const PERSONA_VOICE_CANDIDATE_SAMPLE_COUNT = 3

const GENERATE_CANDIDATES_ERROR_FALLBACK =
  "Could not generate voice candidates. Please try again."
const PREVIEW_ERROR_FALLBACK =
  "Could not load the voice preview. Please try again."

type PersonaVoiceDesignPhase = "idle" | "designing"

function decodeBase64ToBlob(base64: string, mimeType: string): Blob {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
  return new Blob([bytes], { type: mimeType })
}

export function usePersonaVoiceDesign() {
  const [phase, setPhase] = useState<PersonaVoiceDesignPhase>("idle")
  const [candidates, setCandidates] = useState<string[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [keepingEmbeddingId, setKeepingEmbeddingId] = useState<string | null>(
    null,
  )
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(new Map())
  const [previewErrorIds, setPreviewErrorIds] = useState<Set<string>>(new Set())
  const previewUrlsRef = useRef(previewUrls)
  previewUrlsRef.current = previewUrls
  const inFlightPreviewIdsRef = useRef<Set<string>>(new Set())
  const previewRequestGenerationRef = useRef(0)
  const generationRequestIdRef = useRef(0)
  const keepingEmbeddingIdRef = useRef<string | null>(null)

  const { mutateAsync: generateCandidatesRequest } = useTransportMutation(
    transport.operations.generatePersonaVoiceCandidates,
  )
  const { mutateAsync: fetchPreviewRequest } = useTransportMutation(
    transport.operations.previewPersonaVoiceCandidate,
  )
  const { mutateAsync: keepCandidateRequest } = useTransportMutation(
    transport.operations.keepPersonaVoiceCandidate,
  )

  const revokePreviewUrls = useCallback(() => {
    previewRequestGenerationRef.current += 1
    for (const url of previewUrlsRef.current.values()) {
      URL.revokeObjectURL(url)
    }
    previewUrlsRef.current = new Map()
    setPreviewUrls(previewUrlsRef.current)
    setPreviewErrorIds(new Set())
    inFlightPreviewIdsRef.current = new Set()
  }, [])

  useEffect(() => {
    return () => {
      previewRequestGenerationRef.current += 1
      for (const url of previewUrlsRef.current.values()) {
        URL.revokeObjectURL(url)
      }
      previewUrlsRef.current = new Map()
      inFlightPreviewIdsRef.current = new Set()
    }
  }, [])

  const generateCandidates = useCallback(
    async (prompt: string, language: PersonaVoiceDesignLanguage) => {
      const requestId = ++generationRequestIdRef.current
      revokePreviewUrls()
      setCandidates([])
      setIsGenerating(true)
      try {
        const response = await generateCandidatesRequest({
          prompt,
          language,
          n_samples: PERSONA_VOICE_CANDIDATE_SAMPLE_COUNT,
        })
        if (requestId === generationRequestIdRef.current) {
          setCandidates(
            response.candidates.map((candidate) => candidate.embedding_id),
          )
        }
      } catch (error) {
        if (requestId !== generationRequestIdRef.current) {
          return
        }
        log.error("Error generating persona voice candidates", error)
        toast.error({
          error,
          fallback: GENERATE_CANDIDATES_ERROR_FALLBACK,
          title: "Error generating voices",
        })
      } finally {
        if (requestId === generationRequestIdRef.current) {
          setIsGenerating(false)
        }
      }
    },
    [generateCandidatesRequest, revokePreviewUrls],
  )

  const fetchPreview = useCallback(
    async (voiceId: string) => {
      const requestGeneration = previewRequestGenerationRef.current
      try {
        const response = await fetchPreviewRequest({
          voice_id: voiceId,
          text: PERSONA_VOICE_AUDITION_TEXT,
        })
        const blobUrl = URL.createObjectURL(
          decodeBase64ToBlob(response.audio_base64, response.mime_type),
        )
        if (requestGeneration !== previewRequestGenerationRef.current) {
          URL.revokeObjectURL(blobUrl)
          return
        }
        previewUrlsRef.current = new Map(previewUrlsRef.current).set(
          voiceId,
          blobUrl,
        )
        setPreviewUrls(previewUrlsRef.current)
        setPreviewErrorIds((current) => {
          const next = new Set(current)
          next.delete(voiceId)
          return next
        })
      } catch (error) {
        if (requestGeneration !== previewRequestGenerationRef.current) {
          return
        }
        log.error("Error previewing persona voice", error)
        toast.error({
          error,
          fallback: PREVIEW_ERROR_FALLBACK,
          title: "Error loading preview",
        })
        setPreviewErrorIds((current) => new Set(current).add(voiceId))
      } finally {
        if (requestGeneration === previewRequestGenerationRef.current) {
          inFlightPreviewIdsRef.current.delete(voiceId)
        }
      }
    },
    [fetchPreviewRequest],
  )

  const getPreviewUrl = useCallback(
    (voiceId: string): string | undefined => {
      const cached = previewUrlsRef.current.get(voiceId)
      if (cached) {
        return cached
      }
      if (inFlightPreviewIdsRef.current.has(voiceId)) {
        return undefined
      }
      inFlightPreviewIdsRef.current.add(voiceId)
      setPreviewErrorIds((current) => {
        const next = new Set(current)
        next.delete(voiceId)
        return next
      })
      void fetchPreview(voiceId)
      return undefined
    },
    [fetchPreview],
  )

  const keepCandidate = useCallback(
    async (
      embeddingId: string,
      name: string,
      description: string,
    ): Promise<KeepPersonaVoiceCandidateResponse> => {
      if (keepingEmbeddingIdRef.current !== null) {
        throw new Error("A custom voice is already being selected.")
      }
      keepingEmbeddingIdRef.current = embeddingId
      setKeepingEmbeddingId(embeddingId)
      try {
        return await keepCandidateRequest({
          embedding_id: embeddingId,
          name,
          description,
        })
      } finally {
        keepingEmbeddingIdRef.current = null
        setKeepingEmbeddingId(null)
      }
    },
    [keepCandidateRequest],
  )

  const startDesigning = useCallback(() => setPhase("designing"), [])

  const cancelDesigning = useCallback(() => {
    generationRequestIdRef.current += 1
    setPhase("idle")
    setCandidates([])
    setIsGenerating(false)
    revokePreviewUrls()
  }, [revokePreviewUrls])

  return {
    phase,
    candidates,
    isGenerating,
    keepingEmbeddingId,
    previewUrls,
    previewErrorIds,
    getPreviewUrl,
    generateCandidates,
    keepCandidate,
    startDesigning,
    cancelDesigning,
  }
}
